/**
 * Ledger Grid Component
 * Data grid for reservation financials with search, sort, columns, export
 * Ledger-based redesign
 */

import React, { useState, useMemo, useEffect } from 'react';
import { formatDateOnlyET } from '../../utils/dateTimeET.js';
import Tooltip from '../shared/Tooltip.jsx';

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// SINGLE COLUMN MODEL - Single source of truth for all column definitions
const COLUMN_REGISTRY = {
  guest: {
    id: 'guest',
    label: 'Guest Name',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.guest_name ?? '—',
    format: 'text',
    summable: false,
  },
  reservation_id: {
    id: 'reservation_id',
    label: 'Reservation ID',
    sortable: true,
    guestView: false,
    reservationView: true,
    valueGetter: (row) => row.reservation_id ?? '—',
    format: 'text',
    summable: false,
  },
  reservations: {
    id: 'reservations',
    label: '# of Reservations',
    sortable: true,
    guestView: true,
    reservationView: false,
    valueGetter: (row) => row.reservation_count ?? 0,
    format: 'number',
    summable: false, // Count, not sum
  },
  checkin: {
    id: 'checkin',
    label: 'Check In', // Will be overridden for guest view
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row, viewMode) => {
      if (viewMode === 'guest') {
        return row.last_stay_date || row.checkin_date || null;
      }
      return row.checkin_date || null;
    },
    format: 'date',
    summable: false,
  },
  checkout: {
    id: 'checkout',
    label: 'Check Out',
    sortable: true,
    guestView: false,
    reservationView: true,
    valueGetter: (row) => row.checkout_date || null,
    format: 'date',
    summable: false,
  },
  nights: {
    id: 'nights',
    label: 'Nights',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.nights ?? 0,
    format: 'number',
    summable: true,
  },
  price_per_night: {
    id: 'price_per_night',
    label: 'Price Per Night',
    tooltip: 'Net Base Rate divided by stay nights; discounts are already included.',
    sortable: true,
    guestView: false,
    reservationView: true,
    valueGetter: (row) => {
      const nights = row.nights || 0;
      const baseRate = Object.hasOwn(row, 'owner_net_base_rate_total') ? row.owner_net_base_rate_total : row.owner_base_rate_total || 0;
      return nights > 0 && baseRate != null ? baseRate / nights : null;
    },
    format: 'currency',
    summable: false, // Average, not sum
  },
  num_guests: {
    id: 'num_guests',
    label: '# of Guests',
    sortable: true,
    guestView: false,
    reservationView: true,
    valueGetter: (row) => row.num_guests ?? row.guest_count ?? null,
    format: 'number',
    summable: false, // Not meaningful to sum
  },
  channel: {
    id: 'channel',
    label: 'Channel',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.master_channel_label ?? '—',
    format: 'text',
    summable: false,
  },
  base_rate: {
    id: 'base_rate', label: 'Net Base Rate', sortable: true, guestView: true, reservationView: true,
    valueGetter: row => Object.hasOwn(row, 'owner_net_base_rate_total') ? row.owner_net_base_rate_total : row.owner_base_rate_total || 0,
    format: 'currency', summable: true,
    tooltip: 'Base rate after discounts. Already-included discounts are counted once. A dash means the amount needs review. By-night reports allocate the selected stay nights.',
  },
  gross_base_rate: {
    id: 'gross_base_rate', label: 'Gross Base Rate', sortable: true, guestView: true, reservationView: true,
    valueGetter: row => row.owner_gross_base_rate_total ?? null,
    format: 'currency', summable: true, tooltip: 'Base rate before discounts.',
  },
  discounts: {
    id: 'discounts', label: 'Discounts', sortable: true, guestView: true, reservationView: true,
    valueGetter: row => row.owner_discounts_total ?? null,
    format: 'currency', summable: true, tooltip: 'Signed discounts included in Net Base Rate. Do not subtract these again.',
  },
  discount_treatment: {
    id: 'discount_treatment', label: 'Discount Treatment', sortable: true, guestView: true, reservationView: true,
    valueGetter: row => ({ applied: 'Included in net base', already_netted: 'Included in net base', no_discount: 'No discount', combined: 'Combined', unresolved: 'Needs review', source_unavailable: 'Unavailable' }[row.owner_discount_status] ?? '—'),
    format: 'text', summable: false,
  },
  cleaning_fee: {
    id: 'cleaning_fee',
    label: 'Cleaning Fee',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.owner_cleaning_fee_total || 0,
    format: 'currency',
    summable: true,
    tooltip: 'Amounts shown are for stays within the selected period. Reservations spanning months are prorated by nights in range.',
  },
  channel_fees: {
    id: 'channel_fees',
    label: 'Channel Fees',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => {
      if (row.owner_channel_fees_available === false) return null;
      const canonical = row.canonical_financial_summary || {};
      const ownerPayout = canonical.owner_payout || {};
      return ownerPayout.owner_channel_fees ?? row.owner_channel_fees_total ?? 0;
    },
    format: 'currency',
    summable: true,
    tooltip: 'Owner-borne channel fees. Shown as — when not provided (e.g. some VRBO integrated reservations).',
  },
  tourist_tax: {
    id: 'tourist_tax',
    label: 'Tourist Tax Due',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => {
      const canonical = row.canonical_financial_summary || {};
      return canonical.guest_paid?.guest_tourist_tax || row.tourist_tax_total || 0;
    },
    format: 'currency',
    summable: true,
  },
  state_tax: {
    id: 'state_tax',
    label: 'State Tax',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.state_tax_total ?? 0,
    format: 'currency',
    summable: true,
    tooltip: 'State portion of tourist tax (guest-paid).',
  },
  county_tax: {
    id: 'county_tax',
    label: 'County Tax',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.county_tax_total ?? 0,
    format: 'currency',
    summable: true,
    tooltip: 'County portion of tourist tax (guest-paid).',
  },
  clc_fee: {
    id: 'clc_fee',
    label: 'CLC Fee',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => row.clcfee_total || 0,
    format: 'currency',
    summable: true,
  },
  owner_payout: {
    id: 'owner_payout',
    label: 'Owner Revenue',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => {
      const rev = row.owner_revenue_total;
      return (rev != null && rev !== '') ? Number(rev) : 0;
    },
    format: 'currency',
    summable: true,
    tooltip: 'Owner net revenue after deductions (e.g., CLC fee, tourist tax).',
  },
  amount_deposited: {
    id: 'amount_deposited',
    label: 'Amount Deposited',
    sortable: true,
    guestView: true,
    reservationView: true,
    valueGetter: (row) => {
      const canonical = row.canonical_financial_summary || {};
      const ownerPayout = canonical.owner_payout || {};
      const payout = ownerPayout.owner_payout_total;
      return (payout != null && payout !== '') ? Number(payout) : null;
    },
    format: 'currency',
    summable: true,
    tooltip: 'Same as Paid to Owner. Amount deposited to the owner before final deductions (e.g., CLC fee).',
  },
  status: {
    id: 'status',
    label: 'Status',
    sortable: true,
    guestView: false,
    reservationView: true,
    valueGetter: (row) => row.status || '—',
    format: 'text',
    summable: false,
  },
  tax_status: {
    id: 'tax_status',
    label: 'Tax Status',
    sortable: false,
    guestView: true,
    reservationView: true,
    valueGetter: () => null, // Placeholder
    format: 'text',
    summable: false,
    placeholder: true,
  },
};

// Get all column keys
const ALL_COLUMN_KEYS = Object.keys(COLUMN_REGISTRY);

// Presets reference column IDs from registry
const PRESETS = {
  'owner-summary': {
    label: 'Owner Summary',
    columns: ['guest', 'checkin', 'checkout', 'channel', 'base_rate', 'price_per_night', 'amount_deposited', 'owner_payout'],
  },
  detailed: {
    label: 'Financial Detail',
    columns: ['guest', 'reservation_id', 'checkin', 'checkout', 'nights', 'price_per_night', 'num_guests', 'channel', 'gross_base_rate', 'discounts', 'base_rate', 'cleaning_fee', 'channel_fees', 'tourist_tax', 'clc_fee', 'owner_payout', 'status'],
  },
  'tax-compliance': {
    label: 'Tax & Compliance',
    columns: ['guest', 'checkin', 'channel', 'owner_payout', 'channel_fees', 'tourist_tax', 'state_tax', 'county_tax', 'tax_status'],
  },
  operations: {
    label: 'Operations',
    columns: ['guest', 'reservation_id', 'checkin', 'checkout', 'nights', 'num_guests', 'channel', 'owner_payout', 'status'],
  },
};

// Guest Summary preset
const GUEST_SUMMARY_PRESET = {
  'guest-summary': {
    label: 'Guest Summary',
    columns: ['guest', 'reservations', 'nights', 'channel', 'gross_base_rate', 'discounts', 'base_rate', 'cleaning_fee', 'channel_fees', 'tourist_tax', 'clc_fee', 'owner_payout'],
  },
};

// Export for FinancialsPage compact header (preset/column picker in page header)
export { PRESETS, GUEST_SUMMARY_PRESET, ALL_COLUMN_KEYS, COLUMN_REGISTRY };

export default function LedgerGrid({
  reservations = [],
  onRowClick = null,
  onGuestNameClick = null,
  onExportCSV = null,
  propertyName,
  periodLabel,
  viewMode = 'reservation', // 'reservation' | 'guest'
  // Controlled from FinancialsPage when toolbar is in page header
  searchQuery: searchQueryProp,
  hideToolbar = false,
  presetId: presetIdProp,
  onPresetChange,
  visibleColumns: visibleColumnsProp,
  onToggleColumn,
  channelFeesSomeUnavailable = false,
}) {
  const [internalSearch, setInternalSearch] = useState('');
  const [sortColumn, setSortColumn] = useState('checkin');
  const [sortDirection, setSortDirection] = useState('desc');
  const [internalPresetId, setInternalPresetId] = useState('owner-summary');
  const [internalColumnPickerOpen, setInternalColumnPickerOpen] = useState(false);
  
  // Use guest summary preset when viewMode is 'guest'
  const activePresets = viewMode === 'guest' ? GUEST_SUMMARY_PRESET : PRESETS;
  const defaultPresetId = viewMode === 'guest' ? 'guest-summary' : 'owner-summary';
  
  const [internalVisibleColumns, setInternalVisibleColumns] = useState(() => new Set(activePresets[defaultPresetId]?.columns ?? PRESETS['owner-summary'].columns));

  const searchQuery = searchQueryProp !== undefined ? searchQueryProp : internalSearch;
  const presetId = presetIdProp !== undefined ? presetIdProp : internalPresetId;
  const visibleColumns = visibleColumnsProp !== undefined ? visibleColumnsProp : internalVisibleColumns;
  const columnPickerOpen = internalColumnPickerOpen;

  useEffect(() => {
    if (hideToolbar) return;
    const newPresetId = viewMode === 'guest' ? 'guest-summary' : 'owner-summary';
    const presets = viewMode === 'guest' ? GUEST_SUMMARY_PRESET : PRESETS;
    setInternalPresetId(newPresetId);
    setInternalVisibleColumns(new Set(presets[newPresetId]?.columns ?? PRESETS['owner-summary'].columns));
  }, [viewMode, hideToolbar]);

  // Fix: Watch presetId changes and update visibleColumns (internal only when not hideToolbar)
  useEffect(() => {
    if (hideToolbar && visibleColumnsProp !== undefined) return;
    const presets = viewMode === 'guest' ? GUEST_SUMMARY_PRESET : PRESETS;
    const preset = presets[presetId];
    if (preset) {
      setInternalVisibleColumns(new Set(preset.columns));
    }
  }, [presetId, viewMode, hideToolbar, visibleColumnsProp]);

  const tableContainerStyle = useMemo(() => ({
    ...styles.tableContainer,
    overflowX: 'auto',
    minWidth: 0,
  }), []);

  // Filter and sort reservations
  const processedReservations = useMemo(() => {
    let filtered = reservations;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const guestName = (r.guest_name || '').toLowerCase();
        const reservationId = r.reservation_id ? r.reservation_id.toString().toLowerCase() : '';
        const channel = (r.master_channel_label || '').toLowerCase();
        return guestName.includes(query) || reservationId.includes(query) || channel.includes(query);
      });
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let aVal, bVal;

      // Use column registry valueGetter for sorting
      const colDef = COLUMN_REGISTRY[sortColumn];
      if (colDef && colDef.valueGetter) {
        aVal = colDef.valueGetter(a, viewMode);
        bVal = colDef.valueGetter(b, viewMode);
        
        // Handle different formats
        if (colDef.format === 'date') {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        } else if (colDef.format === 'currency' || colDef.format === 'number') {
          aVal = typeof aVal === 'number' ? aVal : 0;
          bVal = typeof bVal === 'number' ? bVal : 0;
        } else {
          // Text format
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }
      } else {
        return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [reservations, searchQuery, sortColumn, sortDirection, viewMode]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    if (!onExportCSV) return;

    // Guest Paid removed from export headers
    const headers = viewMode === 'guest'
      ? ['Guest', 'Reservations', 'Nights', 'Channel', 'Owner Payout', 'Channel Fees', 'Tourist Tax']
      : ['Guest', 'Reservation ID', 'Check-in', 'Nights', 'Channel', 'Owner Payout', 'Channel Fees', 'Tourist Tax'];

    const rows = processedReservations.map(r => {
      const canonical = r.canonical_financial_summary || {};
      const ownerPayout = canonical.owner_payout || {};
      const touristTax = canonical.guest_paid?.guest_tourist_tax || r.tourist_tax_total || 0;

      if (viewMode === 'guest') {
        return [
          r.guest_name || '',
          r.reservation_count || 0,
          r.nights || 0,
          r.master_channel_label || '',
          ownerPayout.owner_payout_total || 0,
          ownerPayout.owner_channel_fees || 0,
          touristTax,
        ];
      } else {
        return [
          r.guest_name || '',
          r.reservation_id || '',
          r.checkin_date ? formatDateOnlyET(r.checkin_date, { short: true }) : '',
          r.nights || 0,
          r.master_channel_label || '',
          ownerPayout.owner_payout_total || 0,
          ownerPayout.owner_channel_fees || 0,
          touristTax,
        ];
      }
    });

    headers.push('Gross Base Rate', 'Discounts', 'Net Base Rate', 'Discount Treatment');
    rows.forEach((row, i) => row.push(processedReservations[i].owner_gross_base_rate_total ?? '', processedReservations[i].owner_discounts_total ?? '', processedReservations[i].owner_net_base_rate_total ?? '', COLUMN_REGISTRY.discount_treatment.valueGetter(processedReservations[i])));
    onExportCSV(headers, rows);
  };

  // Derive columnDefinitions from COLUMN_REGISTRY (single source of truth)
  const columnDefinitions = useMemo(() => {
    // Keep the agreed summary order; optional columns remain available after it.
    const summaryOrder = PRESETS['owner-summary'].columns;
    const keys = viewMode === 'reservation' && presetId === 'owner-summary'
      ? [...summaryOrder, ...ALL_COLUMN_KEYS.filter(key => !summaryOrder.includes(key))]
      : ALL_COLUMN_KEYS;
    return keys.map(key => {
      const col = COLUMN_REGISTRY[key];
      if (!col) return null;
      
      // Filter based on view mode
      if (viewMode === 'guest' && !col.guestView) return null;
      if (viewMode === 'reservation' && !col.reservationView) return null;
      
      // Override label for checkin in guest view
      const label = (key === 'checkin' && viewMode === 'guest') ? 'Last Stay'
        : (key === 'owner_payout' && viewMode === 'reservation' && presetId === 'owner-summary') ? 'Owner Revenue (Net)' : col.label;
      
      return {
        key: col.id,
        label,
        sortable: col.sortable,
        guestView: col.guestView,
        reservationView: col.reservationView,
        format: col.format,
        summable: col.summable,
        valueGetter: col.valueGetter,
      };
    }).filter(Boolean);
  }, [viewMode, presetId]);

  const toggleColumnFn = (key) => {
    if (onToggleColumn) {
      onToggleColumn(key);
    } else {
      setInternalVisibleColumns((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    }
  };

  const setPresetIdFn = (id) => {
    if (onPresetChange) onPresetChange(id);
    else setInternalPresetId(id);
  };

  return (
    <div style={{ ...styles.container, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} data-debug-visible-columns={Array.from(visibleColumns).join(',')}>
      {/* Search + Presets + Column picker + Export - hidden when toolbar is in page header */}
      {!hideToolbar && (
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder={viewMode === 'guest' ? "Search by guest or channel..." : "Search by guest, reservation ID, or channel..."}
          value={searchQuery}
          onChange={(e) => setInternalSearch(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }} htmlFor="ledger-preset">Preset:</label>
          <select
            id="ledger-preset"
            value={presetId}
            onChange={(e) => setPresetIdFn(e.target.value)}
            style={{ ...styles.exportButton, padding: '6px 10px' }}
          >
            {Object.entries(activePresets).map(([id, p]) => (
              <option key={id} value={id}>{p.label}</option>
            ))}
          </select>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setInternalColumnPickerOpen((o) => !o)}
              style={styles.exportButton}
              aria-label="Choose columns"
            >
              Columns
            </button>
            {columnPickerOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  minWidth: 180,
                  background: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  padding: 'var(--spacing-2)',
                }}
              >
                {ALL_COLUMN_KEYS.filter(key => {
                  // Filter out guest_paid (removed)
                  if (key === 'guest_paid') return false;
                  // Filter based on view mode using registry
                  const col = COLUMN_REGISTRY[key];
                  if (!col) return false;
                  if (viewMode === 'guest' && !col.guestView) return false;
                  if (viewMode === 'reservation' && !col.reservationView) return false;
                  return true;
                }).map((key) => {
                  const col = COLUMN_REGISTRY[key];
                  const isPlaceholder = col?.placeholder === true;
                  return (
                    <label 
                      key={key} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        cursor: isPlaceholder ? 'not-allowed' : 'pointer', 
                        padding: '4px 0',
                        opacity: isPlaceholder ? 0.6 : 1,
                      }}
                      title={isPlaceholder ? 'Coming soon' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(key)}
                        onChange={() => !isPlaceholder && toggleColumnFn(key)}
                        disabled={isPlaceholder}
                      />
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>
                        {col?.label ?? key}
                        {isPlaceholder && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginLeft: 4 }}>(soon)</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {onExportCSV && processedReservations.length > 0 && (
          <button onClick={handleExportCSV} style={styles.exportButton} aria-label="Export to CSV">
            Export CSV ({processedReservations.length} rows)
          </button>
        )}
      </div>
      )}

      {/* Table - single scroll container (flex: 1, overflow: auto) so header can be sticky; horizontally scrollable when many columns */}
      <div style={{ ...tableContainerStyle, flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ ...styles.table, minWidth: 'max-content' }}>
          <thead style={styles.theadSticky}>
            <tr>
              {columnDefinitions.map(col => {
                if (!visibleColumns.has(col.key)) return null;
                const colDef = COLUMN_REGISTRY[col.key];
                const tooltipContent = colDef?.tooltip;
                return (
                  <th
                    key={col.key}
                    style={styles.th}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    className={col.sortable ? styles.sortableTh : ''}
                  >
                    {tooltipContent ? (
                      <Tooltip content={tooltipContent} asButton={true}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1, 4px)' }}>
                          {col.label}
                          <span style={{ opacity: 0.8, fontSize: 'var(--font-size-xs)', cursor: 'help' }} aria-hidden="true">ⓘ</span>
                        </span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                    {col.sortable && sortColumn === col.key && (
                      <span style={styles.sortIndicator}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {processedReservations.length === 0 ? (
              <tr>
                <td colSpan={columnDefinitions.filter(c => visibleColumns.has(c.key)).length || 1} style={styles.emptyCell}>
                  {searchQuery 
                    ? (viewMode === 'guest' ? 'No guests match your search' : 'No reservations match your search')
                    : (viewMode === 'guest' ? 'No guests in this period' : 'No reservations in this period')}
                </td>
              </tr>
            ) : (
              processedReservations.map((reservation, index) => {
                const rowKey = reservation.reservation_id || `guest-${index}`;
                return (
                  <tr key={rowKey} style={styles.tr}>
                    {columnDefinitions.map(col => {
                      if (!visibleColumns.has(col.key)) return null;
                      const colDef = COLUMN_REGISTRY[col.key];
                      if (!colDef) return null;
                      
                      const rawValue = colDef.valueGetter(reservation, viewMode);
                      let displayValue = '—';
                      
                      if (col.key === 'guest' && onGuestNameClick && viewMode === 'reservation' && reservation.reservation_id) {
                        const guestLabel = (rawValue !== null && rawValue !== undefined && rawValue !== '') ? String(rawValue) : '—';
                        displayValue = (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onGuestNameClick(reservation); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGuestNameClick(reservation); } }}
                            style={styles.guestNameLink}
                            aria-label={`View reservation ${reservation.reservation_id} details`}
                          >
                            {guestLabel}
                          </button>
                        );
                      } else if (rawValue === null || rawValue === undefined) {
                        if (colDef.placeholder) {
                          displayValue = <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Coming soon</span>;
                        } else if (col.key === 'channel_fees' && reservation.owner_channel_fees_available === false) {
                          displayValue = (
                            <Tooltip content="Owner-borne channel fees not provided for this reservation." asButton={false}>
                              <span style={{ cursor: 'help' }}>—</span>
                            </Tooltip>
                          );
                        } else {
                          displayValue = '—';
                        }
                      } else if (colDef.format === 'currency') {
                        displayValue = formatCurrency(rawValue);
                      } else if (colDef.format === 'date') {
                        displayValue = rawValue ? formatDateOnlyET(rawValue, { short: true }) : '—';
                      } else if (colDef.format === 'number') {
                        displayValue = colDef.id === 'num_guests' && typeof rawValue === 'number'
                          ? Math.round(rawValue)
                          : rawValue;
                      } else {
                        displayValue = rawValue;
                      }
                      
                      return (
                        <td key={col.key} style={styles.td}>{displayValue}</td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
          {/* Totals row; optional footnote row for channel fees */}
          {processedReservations.length > 0 && (() => {
            const visibleColDefs = columnDefinitions.filter(col => visibleColumns.has(col.key));
            const totals = {};
            visibleColDefs.forEach(col => {
              const colDef = COLUMN_REGISTRY[col.key];
              if (colDef && colDef.summable) {
                const sum = processedReservations.reduce((acc, row) => {
                  const value = colDef.valueGetter(row, viewMode);
                  return acc + (typeof value === 'number' ? value : 0);
                }, 0);
                const missingDiscountAmount = ['base_rate', 'gross_base_rate', 'discounts'].includes(col.key) && processedReservations.some(row => colDef.valueGetter(row, viewMode) == null);
                totals[col.key] = missingDiscountAmount ? null : Math.round(sum * 100) / 100;
              }
            });
            const firstTextCol = visibleColDefs.find(col => {
              const colDef = COLUMN_REGISTRY[col.key];
              return colDef && colDef.format === 'text';
            });
            const colCount = visibleColDefs.length || 1;
            const showFootnote = channelFeesSomeUnavailable && visibleColumns.has('channel_fees');
            return (
              <tfoot>
                <tr style={{ ...styles.tr, backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)', fontWeight: 'var(--font-weight-semibold, 600)' }}>
                  {visibleColDefs.map(col => {
                    const colDef = COLUMN_REGISTRY[col.key];
                    let cellContent = '—';
                    if (col.key === firstTextCol?.key) {
                      cellContent = 'Totals';
                    } else if (colDef && colDef.summable && totals[col.key] !== undefined) {
                      if (colDef.format === 'currency') {
                        cellContent = totals[col.key] == null ? '—' : formatCurrency(totals[col.key]);
                      } else if (colDef.format === 'number') {
                        cellContent = totals[col.key];
                      }
                    }
                    return (
                      <td key={col.key} style={styles.td}>{cellContent}</td>
                    );
                  })}
                </tr>
                {showFootnote && (
                  <tr style={{ ...styles.tr, backgroundColor: 'var(--color-bg-secondary, #f9fafb)', fontSize: 'var(--font-size-sm, 14px)', color: 'var(--color-text-secondary, #6b7280)' }}>
                    <td style={styles.td} colSpan={colCount}>
                      <span style={{ fontStyle: 'italic' }}>
                        Some channel fee breakdown values are unavailable for certain VRBO reservations.
                      </span>
                    </td>
                  </tr>
                )}
              </tfoot>
            );
          })()}
        </table>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4, 16px)',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    minWidth: 0, // Allow horizontal scroll within flex parent
  },
  toolbar: {
    display: 'flex',
    gap: 'var(--spacing-3, 12px)',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: 'var(--spacing-3, 12px) 0',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
    marginBottom: 'var(--spacing-2, 8px)',
  },
  searchInput: {
    flex: 1,
    maxWidth: '400px',
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    fontSize: 'var(--font-size-sm, 14px)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
  },
  exportButton: {
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-text-primary, #111827)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    cursor: 'pointer',
  },
  tableContainer: {
    overflowX: 'auto',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
  },
  theadSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
  },
  th: {
    padding: '8px 6px',
    textAlign: 'left',
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    fontSize: 'var(--font-size-sm, 14px)',
    borderBottom: '2px solid var(--color-border-medium, #d1d5db)',
    whiteSpace: 'nowrap',
  },
  sortableTh: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  sortIndicator: {
    marginLeft: '2px',
    fontSize: 'var(--font-size-xs, 12px)',
  },
  tr: {
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
  },
  guestNameLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'var(--color-link, #2563eb)',
    cursor: 'pointer',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  'clickableRow:hover': {
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  },
  td: {
    padding: '6px 6px',
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-primary, #111827)',
  },
  emptyCell: {
    padding: 'var(--spacing-8, 48px)',
    textAlign: 'center',
    color: 'var(--color-text-secondary, #6b7280)',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--spacing-1, 4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    minHeight: '32px',
    borderRadius: 'var(--radius-sm, 4px)',
    transition: 'background-color 0.2s ease',
  },
  expandIcon: {
    fontSize: 'var(--font-size-xs, 12px)',
    color: 'var(--color-text-secondary, #6b7280)',
    transition: 'transform 0.2s ease-out',
  },
  expandedCell: {
    padding: 'var(--spacing-4, 16px)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    borderTop: '2px solid var(--color-border-medium, #d1d5db)',
  },
  expandedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4, 16px)',
  },
};
